import React, { useState, useEffect, useCallback } from 'react';
import { AdventureItem } from '../../types';
import apiService from '../../services/api';
import useGameStore from '../../stores/gameStore';
import IconImage from './IconImage.tsx';

export interface SelectableInventoryListProps {
  characterName?: string;
  locationTag?: string;
  selectionTarget: string;
  onItemSelect: (itemTag: string, targetScene: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Component for displaying selectable character or location inventories
 * Clicking an item calls onItemSelect with the item tag and target scene
 */
export const SelectableInventoryList: React.FC<SelectableInventoryListProps> = ({
  characterName,
  locationTag,
  selectionTarget,
  onItemSelect,
  className = '',
  style
}) => {

  const [items, setItems] = useState<AdventureItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { currentSession, currentPlayer, sessionPlayers = [] } = useGameStore();

  const findPlayerByCharacter = useCallback(
    (targetCharacterName: string | undefined) => {
      if (!targetCharacterName) {
        return undefined;
      }

      const playerFromSession = currentSession?.players?.find(
        (player) => player.characterName === targetCharacterName
      );
      if (playerFromSession) {
        return playerFromSession;
      }

      const playerFromSessionPlayers = sessionPlayers.find(
        (player) => player.characterName === targetCharacterName
      );
      if (playerFromSessionPlayers) {
        return playerFromSessionPlayers;
      }

      if (currentPlayer?.characterName === targetCharacterName) {
        return currentPlayer;
      }

      return undefined;
    },
    [currentSession, sessionPlayers, currentPlayer]
  );

  const loadInventory = useCallback(async () => {
    if (!currentSession) {
      setError('No active session');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let inventoryItems: AdventureItem[] = [];

      if (characterName) {
        // Load character inventory
        const player = findPlayerByCharacter(characterName);
        if (!player) {
          throw new Error(`Character "${characterName}" not found in session`);
        }
        
        // Get player's inventory items
        inventoryItems = await apiService.getPlayerItems(currentSession.id, player.id);
      } else if (locationTag) {
        // Load location inventory using unified inventory API
        const response = await apiService.getInventoryItems(currentSession.id, locationTag, 'location');
        inventoryItems = response.items;
      } else {
        throw new Error('Either characterName or locationTag must be provided');
      }
      setItems(inventoryItems);
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setError(err instanceof Error ? err.message : 'Failed to load inventory');
    } finally {
      setIsLoading(false);
    }
  }, [currentSession, characterName, findPlayerByCharacter, locationTag]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  // Listen for inventory updates and refetch when relevant
  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<any>;
      const detail = custom.detail || {};
      if (!currentSession) {
        return;
      }

      // Match on owner: for character, match by characterName; for location, match tag
      if (characterName && detail.ownerType === 'character') {
        const player = findPlayerByCharacter(characterName);
        if (player && detail.ownerId === player.id) {
          void loadInventory();
        }
      } else if (locationTag && detail.ownerType === 'location') {
        if (detail.ownerId === locationTag) {
          void loadInventory();
        }
      }
    };

    window.addEventListener('inventory:update', handler as EventListener);
    return () => window.removeEventListener('inventory:update', handler as EventListener);
  }, [currentSession, characterName, findPlayerByCharacter, locationTag, loadInventory]);

  const handleItemClick = useCallback((itemTag: string) => {
    onItemSelect(itemTag, selectionTarget);
  }, [onItemSelect, selectionTarget]);

  if (isLoading) {
    return (
      <div className={`selectable-inventory-list loading ${className}`} style={style} data-testid="selectable-inventory-list-loading">
        <div className="inventory-list-header">
          <h4>Loading inventory...</h4>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`selectable-inventory-list error ${className}`} style={style} data-testid="selectable-inventory-list-error">
        <div className="inventory-list-header">
          <h4>Inventory Error</h4>
        </div>
        <div className="inventory-list-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={`selectable-inventory-list empty ${className}`} style={style} data-testid="selectable-inventory-list-empty">
        <div className="inventory-list-empty flex items-center justify-center py-2" data-testid="empty-inventory">
          <p className="italic text-center">No items</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`selectable-inventory-list ${className}`} style={style} data-testid="selectable-inventory-list">
      <div
        className="inventory-list-items icons-only flex flex-wrap justify-center"
        data-testid="inventory-items-list"
      >
        {items.map((item, index) => (
          <button
            key={`${item.tag}-${index}`}
            onClick={() => handleItemClick(item.tag)}
            className="inventory-item-button"
            data-testid={`selectable-inventory-item-${item.tag}`}
            data-item-tag={item.tag}
            aria-label={`Select ${item.name}`}
          >
            <div className="inventory-item-icon">
              {item.iconId && (
                <IconImage imageId={item.iconId} />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SelectableInventoryList;


